import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
  Category as CategoryIcon,
  List as ListIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import AddVisitTypeModal from './AddVisitTypeModal';
import VisitTypeCategories from '../VisitTypeCategories';
import AddVisitTypeCategoryModal from '../VisitTypeCategories/AddVisitTypeCategoryModal';
import { hasPermission } from '../../../utils/permissions';
import {
  bulkDeleteVisitTypeCategories,
  addVisitTypeCategory,
} from '../VisitTypeCategories/slice';
import {
  fetchInit,
  fetchVisitTypes,
  fetchVisitTypeById,
  addVisitType,
  updateVisitType,
  selectVisitTypes,
  selectVisitTypesLoading,
  selectVisitTypesError,
  selectVisitTypesSuccessMessage,
  selectVisitTypesTotal,
  selectVisitTypesPage,
  selectVisitTypesPageSize,
  selectVisitTypesSortColumn,
  selectVisitTypesSortDirection,
  selectVisitTypesFilterName,
  selectSelectedVisitType,
  selectVisitTypeCategories,
  selectActiveTab,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  setActiveTab,
  deleteVisitType,
  bulkDeleteVisitTypes,
  clearError,
  clearSuccessMessage,
} from './slice';

// TabPanel component - defined outside to prevent re-creation on each render
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`visit-types-tabpanel-${index}`}
      aria-labelledby={`visit-types-tab-${index}`}
      style={{ flex: 1, display: value === index ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}
      {...other}
    >
      <Box sx={{ pt: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{children}</Box>
    </div>
  );
}

function VisitTypes() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const visitTypes = useSelector(selectVisitTypes);
  const loading = useSelector(selectVisitTypesLoading);
  const error = useSelector(selectVisitTypesError);
  const successMessage = useSelector(selectVisitTypesSuccessMessage);
  const total = useSelector(selectVisitTypesTotal);
  const page = useSelector(selectVisitTypesPage);
  const pageSize = useSelector(selectVisitTypesPageSize);
  const sortColumn = useSelector(selectVisitTypesSortColumn);
  const sortDirection = useSelector(selectVisitTypesSortDirection);
  const filterName = useSelector(selectVisitTypesFilterName);
  const selectedVisitType = useSelector(selectSelectedVisitType);
  const visitTypeCategories = useSelector(selectVisitTypeCategories);
  const activeTab = useSelector(selectActiveTab);

  // VisitTypeCategories permissions
  const canAddVisitTypeCategory = hasPermission('visittypecategory', 'addVisitTypeCategory');
  const canDeleteVisitTypeCategory = hasPermission('visittypecategory', 'deleteVisitTypeCategory');
  
  // State for VisitTypeCategories buttons
  const [visitTypeCategoriesSelectedIds, setVisitTypeCategoriesSelectedIds] = useState([]);
  const [showVisitTypeCategoryAddDialog, setShowVisitTypeCategoryAddDialog] = useState(false);
  const [showVisitTypeCategoryBulkDeleteDialog, setShowVisitTypeCategoryBulkDeleteDialog] = useState(false);
  
  // Handlers for VisitTypeCategories - use useCallback to prevent re-creation
  const handleAddVisitTypeCategory = useCallback(() => {
    setShowVisitTypeCategoryAddDialog(true);
  }, []);
  
  const handleCloseAddVisitTypeCategory = useCallback(() => {
    setShowVisitTypeCategoryAddDialog(false);
  }, []);
  
  const handleConfirmAddVisitTypeCategory = useCallback((visitTypeCategoryData) => {
    dispatch(addVisitTypeCategory(visitTypeCategoryData));
    setShowVisitTypeCategoryAddDialog(false);
  }, [dispatch]);
  
  const handleBulkDeleteVisitTypeCategories = useCallback(() => {
    setShowVisitTypeCategoryBulkDeleteDialog(true);
  }, []);
  
  // Memoize the setter to prevent re-creation
  const handleVisitTypeCategoriesSelectedIdsChange = useCallback((ids) => {
    setVisitTypeCategoriesSelectedIds(ids);
  }, []);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [visitTypeToDelete, setVisitTypeToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Check permissions
  const canAddVisitType = hasPermission('visittype', 'addVisitType');
  const canEditVisitType = hasPermission('visittype', 'editVisitType');
  const canDeleteVisitType = hasPermission('visittype', 'deleteVisitType');

  // Fetch init data on mount
  useEffect(() => {
    dispatch(fetchInit());
  }, [dispatch]);

  // Fetch data whenever tab changes or when filters change
  useEffect(() => {
    dispatch(fetchVisitTypes({ page, pageSize, sortColumn, sortDirection, name: filterName }));
  }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName, activeTab]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(successMessage, 'success');
      dispatch(clearSuccessMessage());
      // Refetch visit types to update the list
      dispatch(fetchVisitTypes({ page, pageSize, sortColumn, sortDirection, name: filterName }));
    }
  }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName]);

  // Helper function to escape HTML and prevent XSS
  const escapeHtml = (text) => {
    if (!text) return '-';
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    dispatch(setActiveTab(newValue));
    // Reset selectedIds when switching tabs
    setSelectedIds([]);
    setVisitTypeCategoriesSelectedIds([]);
    // Refetch init data to update categories for select in modal
    dispatch(fetchInit());
  };

  // Define table columns
  const columns = [
    {
      key: 'code',
      label: t('visitTypes.table.code'),
      render: (value) => value || '-',
    },
    {
      key: 'name',
      label: t('visitTypes.table.name'),
    },
    {
      key: 'description',
      label: t('visitTypes.table.description'),
      render: (value) => escapeHtml(value),
    },
    {
      key: 'categoryName',
      label: t('visitTypes.table.category'),
      render: (value) => value || '-',
    },
  ];

  // Handle edit action
  const handleEdit = (row) => {
    // Fetch visit type details by ID
    dispatch(fetchVisitTypeById(row.id));
    setShowEditDialog(true);
  };

  // Handle delete action - show confirmation dialog
  const handleDelete = (row) => {
    setVisitTypeToDelete(row);
    setShowDeleteDialog(true);
  };

  // Confirm delete action
  const handleConfirmDelete = () => {
    if (visitTypeToDelete?.id) {
      dispatch(deleteVisitType(visitTypeToDelete.id));
    }
    setShowDeleteDialog(false);
    setVisitTypeToDelete(null);
  };

  // Cancel delete action
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setVisitTypeToDelete(null);
  };

  // Handle bulk delete - show confirmation dialog
  const handleBulkDelete = () => {
    setShowBulkDeleteDialog(true);
  };

  // Confirm bulk delete action
  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeleteVisitTypes(selectedIds));
    setShowBulkDeleteDialog(false);
    setSelectedIds([]);
  };

  // Cancel bulk delete action
  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    dispatch(setPageSize(newPageSize));
  };

  // Handle sort change
  const handleSortChange = (column, direction) => {
    dispatch(setSortColumn(column));
    dispatch(setSortDirection(direction));
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    dispatch(setFilterName(event.target.value));
  };

  // Handle add visit type - show dialog
  const handleAddVisitType = () => {
    setShowAddDialog(true);
  };

  // Handle close add visit type dialog
  const handleCloseAddVisitType = () => {
    setShowAddDialog(false);
  };

  // Confirm add visit type
  const handleConfirmAddVisitType = (visitTypeData) => {
    dispatch(addVisitType(visitTypeData));
    setShowAddDialog(false);
  };

  // Handle close edit visit type dialog
  const handleCloseEditVisitType = () => {
    setShowEditDialog(false);
  };

  // Confirm edit visit type
  const handleConfirmEditVisitType = (visitTypeData) => {
    dispatch(updateVisitType(visitTypeData));
    setShowEditDialog(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BusinessIcon fontSize="small" />
            <Typography variant="body2">{t('visitTypes.breadcrumb.catalog')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('visitTypes.breadcrumb.visitTypes')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title and Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
            {t('visitTypes.title')}
          </Typography>

          {/* Action Buttons - Show based on active tab */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeTab === 0 && (
              <>
                {canAddVisitType && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddVisitType}
                    id="addVisitType"
                    name="addVisitType"
                    data-testid="visitTypes-button-addVisitType"
                  >
                    {t('visitTypes.addVisitType')}
                  </Button>
                )}
                {canDeleteVisitType && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleBulkDelete}
                    disabled={selectedIds.length === 0}
                    id="deleteSelected"
                    name="deleteSelected"
                    data-testid="visitTypes-button-deleteSelected"
                  >
                    {t('visitTypes.deleteSelected')}
                  </Button>
                )}
              </>
            )}
            {activeTab === 1 && (
              <>
                {canAddVisitTypeCategory && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddVisitTypeCategory}
                    id="addVisitTypeCategory"
                    name="addVisitTypeCategory"
                    data-testid="visitTypes-button-addVisitTypeCategory"
                  >
                    {t('visitTypeCategories.addVisitTypeCategory')}
                  </Button>
                )}
                {canDeleteVisitTypeCategory && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleBulkDeleteVisitTypeCategories}
                    disabled={visitTypeCategoriesSelectedIds.length === 0}
                    id="deleteSelectedVisitTypeCategories"
                    name="deleteSelectedVisitTypeCategories"
                    data-testid="visitTypes-button-deleteSelectedVisitTypeCategories"
                  >
                    {t('visitTypeCategories.deleteSelected')}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="visit types tabs">
            <Tab
              icon={<ListIcon />}
              iconPosition="start"
              label={t('visitTypes.tabs.visitTypes')}
              id="visit-types-tab-0"
              aria-controls="visit-types-tabpanel-0"
            />
            <Tab
              icon={<CategoryIcon />}
              iconPosition="start"
              label={t('visitTypes.tabs.visitTypeCategories')}
              id="visit-types-tab-1"
              aria-controls="visit-types-tabpanel-1"
            />
          </Tabs>
        </Box>

        {/* Visit Types Tab */}
        <TabPanel value={activeTab} index={0}>
          {/* Filter Section */}
          <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Input
                  label={t('visitTypes.filterByName')}
                  value={filterName}
                  onChange={handleFilterChange}
                  placeholder={t('visitTypes.filterByNamePlaceholder')}
                  id="filterName"
                  name="filterName"
                  data-testid="visitTypes-input-filterName"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Table */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff', minHeight: 0 }}>
            <Table
              id="visitTypes-table"
              columns={columns}
              data={visitTypes}
              selectable={true}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              onEdit={canEditVisitType ? handleEdit : null}
              onDelete={canDeleteVisitType ? handleDelete : null}
              loading={loading}
              emptyMessage={t('visitTypes.noData')}
              idField="id"
              pagination={true}
              page={page}
              pageSize={pageSize}
              totalItems={total}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              sortable={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              sortableColumns={['code', 'name', 'description', 'categoryName']}
            />
          </Box>
        </TabPanel>

        {/* Visit Type Categories Tab */}
        <TabPanel value={activeTab} index={1}>
          <VisitTypeCategories 
            onSelectedIdsChange={handleVisitTypeCategoriesSelectedIdsChange}
            onAddClick={handleAddVisitTypeCategory}
            onBulkDeleteClick={handleBulkDeleteVisitTypeCategories}
            hideButtons={true}
            isActive={activeTab === 1}
          />
        </TabPanel>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('visitTypes.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('visitTypes.deleteDialog.message', {
              name: visitTypeToDelete?.name || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            id="cancelDelete"
            name="cancelDelete"
            data-testid="visitTypes-button-cancelDelete"
          >
            {t('visitTypes.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            name="confirmDelete"
            data-testid="visitTypes-button-confirmDelete"
          >
            {t('visitTypes.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onClose={handleCancelBulkDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('visitTypes.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('visitTypes.bulkDeleteDialog.message', {
              count: selectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            name="cancelBulkDelete"
            data-testid="visitTypes-button-cancelBulkDelete"
          >
            {t('visitTypes.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            name="confirmBulkDelete"
            data-testid="visitTypes-button-confirmBulkDelete"
          >
            {t('visitTypes.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Visit Type Modal */}
      <AddVisitTypeModal
        open={showAddDialog}
        onClose={handleCloseAddVisitType}
        onConfirm={handleConfirmAddVisitType}
        visitTypeCategories={visitTypeCategories}
      />

      {/* Edit Visit Type Modal */}
      <AddVisitTypeModal
        open={showEditDialog}
        onClose={handleCloseEditVisitType}
        onConfirm={handleConfirmEditVisitType}
        visitTypeData={selectedVisitType}
        visitTypeCategories={visitTypeCategories}
        editMode={true}
      />

      {/* Add Visit Type Category Modal */}
      <AddVisitTypeCategoryModal
        open={showVisitTypeCategoryAddDialog}
        onClose={handleCloseAddVisitTypeCategory}
        onConfirm={handleConfirmAddVisitTypeCategory}
      />

      {/* Confirm Bulk Delete Visit Type Categories Dialog */}
      <Dialog
        open={showVisitTypeCategoryBulkDeleteDialog}
        onClose={() => setShowVisitTypeCategoryBulkDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('visitTypeCategories.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('visitTypeCategories.bulkDeleteDialog.message', {
              count: visitTypeCategoriesSelectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowVisitTypeCategoryBulkDeleteDialog(false)}
            variant="outlined"
            id="cancelBulkDeleteVisitTypeCategories"
            name="cancelBulkDeleteVisitTypeCategories"
            data-testid="visitTypes-button-cancelBulkDeleteVisitTypeCategories"
          >
            {t('visitTypeCategories.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={() => {
              dispatch(bulkDeleteVisitTypeCategories(visitTypeCategoriesSelectedIds));
              setShowVisitTypeCategoryBulkDeleteDialog(false);
              setVisitTypeCategoriesSelectedIds([]);
            }}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDeleteVisitTypeCategories"
            name="confirmBulkDeleteVisitTypeCategories"
            data-testid="visitTypes-button-confirmBulkDeleteVisitTypeCategories"
          >
            {t('visitTypeCategories.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default VisitTypes;
